import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useState } from 'react';
import useSWR from 'swr';
import { fetcher, postFetcher } from 'src/utils/axios';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Profile',
    linkTo: paths.dashboard.user.profile,
  },
  {
    label: 'Settings',
    linkTo: paths.dashboard.user.account,
  },
  {
    label: 'Update Password',
    linkTo: null, // No navigation, opens dialog
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const { user, logout } = useAuthContext();

  const getUserRole = () => {
    if (user?.is_admin) return 'admin';
    if (user?.is_academic) return 'academic';
    if (user?.is_non_academic) return 'non_academic';
    if (user?.is_student) return 'student';
    if (user?.is_parent) return 'parent';
    if (user?.is_height_student) return 'height_student';
    if (user?.is_outsider) return 'outsider';


    return null;
  };

  const currentRole = getUserRole();

  const { enqueueSnackbar } = useSnackbar();
  const popover = usePopover();
  const [openDialog, setOpenDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [openStaffStatusDialog, setOpenStaffStatusDialog] = useState(false);
  const [isUpdatingStaffStatus, setIsUpdatingStaffStatus] = useState(false);

  const { data: staffBulkStatus, mutate: refreshStaffBulkStatus } = useSWR(
    currentRole === 'admin' ? 'accounts/api/staff/bulk-account-status/' : null,
    fetcher
  );

  const shouldActivateStaff = Boolean(staffBulkStatus?.has_bulk_deactivated_accounts);

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path, label) => {
    if (label === 'Update Password') {
      setOpenDialog(true);
    } else if (path) {
      popover.onClose();
      router.push(path);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewPassword('');
  };

  const handleUpdatePassword = async () => {
    // Placeholder for password update logic
    if (newPassword.length < 8) {
      enqueueSnackbar('Password must be at least 8 characters long!', { variant: 'error' });
      return;
    }

    const response = await postFetcher([`accounts/api/update/password/`, { new_password: newPassword }])

    if (response.success) {
      enqueueSnackbar('Password updated successfully!', { variant: 'success' });
      handleCloseDialog();
    }

    if (response.error) {
      enqueueSnackbar('Password not updated!', { variant: 'error' });
    }

  };

  const handleOpenStaffStatusDialog = () => {
    popover.onClose();
    setOpenStaffStatusDialog(true);
  };

  const handleCloseStaffStatusDialog = () => {
    if (!isUpdatingStaffStatus) {
      setOpenStaffStatusDialog(false);
    }
  };

  const handleUpdateAllStaffStatus = async () => {
    setIsUpdatingStaffStatus(true);

    try {
      const endpoint = shouldActivateStaff
        ? 'accounts/api/staff/activate-bulk-deactivated/'
        : 'accounts/api/staff/deactivate-all/';
      const response = await postFetcher([endpoint, {}]);
      const updatedCount = shouldActivateStaff
        ? response.activated_user_count
        : response.deactivated_user_count;
      const action = shouldActivateStaff ? 'activated' : 'deactivated';

      enqueueSnackbar(`${updatedCount} staff account(s) ${action} successfully.`, {
        variant: 'success',
      });
      setOpenStaffStatusDialog(false);
      await refreshStaffBulkStatus();
    } catch (error) {
      const errorMessage =
        error?.detail || error?.error || 'Unable to update staff account status.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsUpdatingStaffStatus(false);
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.avatar_image}
          alt={user?.username}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.avatar_image}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.username}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo, option.label)}>
              {option.label}
            </MenuItem>
          ))}

          {currentRole === 'admin' && (
            <MenuItem
              onClick={handleOpenStaffStatusDialog}
              sx={{
                color: shouldActivateStaff ? 'success.main' : 'error.main',
                whiteSpace: 'normal',
              }}
            >
              {shouldActivateStaff
                ? 'Activate All Staff Accounts'
                : 'Deactivate All Staff Accounts'}
            </MenuItem>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Update Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleUpdatePassword} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openStaffStatusDialog} onClose={handleCloseStaffStatusDialog}>
        <DialogTitle>
          {shouldActivateStaff
            ? 'Activate Bulk-Deactivated Staff Accounts?'
            : 'Deactivate All Staff Accounts?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {shouldActivateStaff
              ? `This will activate only the ${staffBulkStatus?.bulk_deactivated_count || 0} account(s) disabled by the last bulk action. Individually deactivated accounts will remain inactive.`
              : 'This will deactivate every currently active academic and non-academic account. Accounts that were already individually deactivated will remain separate and will not be activated by the bulk action. Admin accounts will remain active.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStaffStatusDialog} disabled={isUpdatingStaffStatus}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleUpdateAllStaffStatus}
            loading={isUpdatingStaffStatus}
            variant="contained"
            color={shouldActivateStaff ? 'primary' : 'error'}
          >
            {shouldActivateStaff ? 'Activate Accounts' : 'Deactivate All Staff'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
